// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  CardActionArea,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { useModuleParty } from '../../contexts/ModuleContext';
import { useUserState } from '../../contexts/UserProvider';
import { TextInputLabel } from '../Form/TextInput';

export const HeaderContent: React.FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { loggedInUser, logout } = useUserState();
  const onLogout = () => {
    logout();
  };
  const party = useModuleParty();

  const { enqueueSnackbar } = useSnackbar();

  const toSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => enqueueSnackbar('Successfully copied to clipboard', { variant: 'success' }))
      .catch(() => enqueueSnackbar('Failed to copy to clipboard', { variant: 'error' }));
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'hidden',
        ml: 'auto',
        pl: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <Tooltip title={`Click to copy Party Id: ${party}`}>
          <Box
            component={Button}
            variant="text"
            size="small"
            sx={{
              ml: 1,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              maxWidth: '200px',
              justifyContent: 'flex-start',
              whiteSpace: 'nowrap',
            }}
            onClick={() => copyToClipboard(party)}
          >
            <Typography color="text.primary" sx={{ mr: 1 }} variant="body2">
              Party ID:
            </Typography>
            <Typography
              sx={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                textTransform: 'lowercase',
              }}
              variant="body2"
            >
              {party}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Account">
          <IconButton
            aria-label="account"
            sx={{ display: 'flex', alignItems: 'center' }}
            onClick={handleClick}
          >
            <AccountCircleIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ maxWidth: '300px', p: 2 }}>
          <Box sx={{ mt: 1 }}>
            <TextInputLabel label="Party ID" />
            <CardActionArea
              onClick={() => copyToClipboard(party)}
              sx={{
                textWrap: 'wrap',
                cursor: 'pointer',
                border: (t) => `1px solid ${t.palette.divider}`,
                p: 1,
                borderRadius: 2,
                position: 'relative',
              }}
            >
              <Tooltip title="Click to copy">
                <Typography
                  variant="caption"
                  sx={{ wordBreak: 'break-word', fontFamily: 'monospace' }}
                >
                  {party}
                </Typography>
              </Tooltip>
              <ContentCopyIcon
                color="action"
                sx={{ position: 'absolute', right: 4, bottom: 4 }}
                fontSize="small"
              />
            </CardActionArea>
            <Box sx={{ mt: 1 }}>
              <TextInputLabel label="User ID" />
              <Typography>{loggedInUser?.userId}</Typography>
            </Box>
            <Box sx={{ mt: 1 }}>
              <TextInputLabel label="Version" />
              <Typography variant="caption" color="text.secondary">
                {__APP_VERSION__}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <MenuList>
          <MenuItem onClick={toSettings}>
            <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Settings</Typography>
          </MenuItem>
          <MenuItem aria-label="Log out" onClick={onLogout}>
            <LogoutIcon color="error" fontSize="small" sx={{ mr: 1 }} />
            <Typography color="error" variant="body2">
              Log out
            </Typography>
          </MenuItem>
        </MenuList>
      </Popover>
    </Box>
  );
};
